import type { RootState } from "../state";
import { message } from "@/utils/message/messageFactory";
import {
  saveLiveConfig,
  insertUrgent,
  startProductsLive,
  endProductsLive,
  getExternalRoomId as getExternalRoomIdApi,
  mockAsk as mockAskApi
} from "@/api/live";
// import { useUserStoreHook } from "@/store/modules/user";
import { emitter } from "@/utils/emitter/mitt";
// import { augmentStoreWithAppleHtml5Chain } from "./appleHtml5ChainPlayer";
import { augmentStoreWithAppleHiddenWebAudio } from "./appleBackgroundWebAudio";
import { delay } from "@/utils/timerUtils";
import { $t } from "@/plugins/i18n";
import { translateSingleText } from "@/utils/live";
// API调用相关方法

export async function saveConfig(this: RootState & { [key: string]: any }) {
  if (!this.currentRoomState) return false;
  const state = this.currentRoomState;

  state.saveConfigLoading = true;

  // 为新建直播间准备正确的参数
  const isNewRoom = this.currentRoomId === "NEW_ROOM";

  // 验证选中的音色是否有效（存在于当前音色列表中）
  let validVoiceProfileCode = "";
  if (state.selectedVoice && state.voiceList) {
    const isVoiceValid = state.voiceList.some(
      voice => voice.id === state.selectedVoice.id
    );
    if (isVoiceValid) {
      validVoiceProfileCode = state.selectedVoice.voiceCode || "";
    } else {
      console.warn("保存时发现选中的音色无效，已清除音色配置");
      state.selectedVoice = null;
      state.selectedVoiceId = null;
    }
  }

  // 因为在init 的时候会根据promotions来恢复products 虽然不知道干啥用的但是我只能在保存的时候重新赋值它了
  if (state.productList.products.length) {
    state.productList.promotions = state.productList.products;
  }
  const params = {
    roomName: state.productList.name,
    // 修正：将用户选择的直播平台传给platformCode字段
    platformCode: state.livePlatform || "抖音",
    language:
      state.productList.id === "NEW_ROOM"
        ? state.productDetail.language
        : state.selectedVoice?.language ||
          state.voiceLangs[state.activeLanguageIdx]?.language ||
          state.productList.language,
    voiceProfileCode:
      state.productList.id === "NEW_ROOM" ? "" : validVoiceProfileCode,
    productIds: state.productList.products.map(item => item.id),
    // 新增：保存直播链接到roomLink字段
    roomLink: state.roomLink || "",
    // 🚨 新增：保存轮播时间配置
    intervalTime: state.carouselInterval
      ? parseInt(state.carouselInterval)
      : null,
    // 新建直播间不传递roomId，让服务器生成
    ...(isNewRoom ? {} : { roomId: state.roomId || state.productList.id }),
    productId: state.selectedProductId,
    ...state.liveStyle
  };

  try {
    const res = await saveLiveConfig(params);

    if (res && res.success) {
      const newRoomId = res.result.id;

      // 🔄 处理新建直播间的状态迁移
      if (
        this.currentRoomId === "NEW_ROOM" &&
        newRoomId &&
        newRoomId !== "NEW_ROOM"
      ) {
        // 复制状态到新的roomId
        const tempState = this.roomStates.get("NEW_ROOM");
        if (tempState) {
          this.roomStates.set(newRoomId, { ...tempState });
          const newState = this.roomStates.get(newRoomId);
          newState.roomId = newRoomId;
          newState.productList.id = newRoomId;
          newState.productList.language = res.result.language;
          newState.productList.voiceProfileCode = res.result.voiceProfileCode;
          newState.saveConfigLoading = false;
          // newState.startLiveLoading = false;

          // 删除临时状态
          this.roomStates.delete("NEW_ROOM");

          // 更新当前roomId
          this.currentRoomId = newRoomId;
        }
      } else {
        // 现有直播间，正常更新roomId
        state.roomId = newRoomId;
        state.productList.language = res.result.language;
        state.productList.voiceProfileCode = res.result.voiceProfileCode;
      }

      message.success(
        res.message || $t("msg.configSaved"),
        res.message ? false : true
      );
      this.fetchInitialVoiceData();
      return true;
    } else {
      console.error(`❌ 保存配置失败:`, res);
      message.error(
        res?.message || $t("msg.saveConfigFailed"),
        res.message ? false : true
      );
      return false;
    }
  } catch (e) {
    console.error("❌ 保存配置异常：", e);
    message.error($t("msg.saveConfigFailed"), true);
    return false;
  } finally {
    state.saveConfigLoading = false;
  }
}

export async function startLive(this: RootState & { [key: string]: any }) {
  if (!this.currentRoomState) return;
  const state = this.currentRoomState;

  // 移除权限检查，直接开始直播

  state.startLiveLoading = true;
  try {
    const ok = await this.saveConfig();
    if (!ok) {
      // 修复：保存配置失败时，确保重置loading状态
      state.startLiveLoading = false;
      return;
    }

    // 🍎 首播预热：在用户点击事件栈内尽早建立 Now Playing
    try {
      const rid = state.roomId || state.productList.id;
      this.prewarmNowPlayingForApple && this.prewarmNowPlayingForApple(rid);
      // 🍎 Apple-only: 强制使用 WebAudio 分支
      try {
        const ua = navigator.userAgent || "";
        const isIOS =
          /iPhone|iPad|iPod/i.test(ua) ||
          (/Macintosh/.test(ua) && (navigator as any).maxTouchPoints > 1);
        const isSafari =
          /Safari/i.test(ua) && !/Chrome|Chromium|Edge/i.test(ua);
        const appleLike = isIOS || isSafari;
        if (appleLike) {
          try {
            augmentStoreWithAppleHiddenWebAudio(this as any);
          } catch {}
          try {
            (this as any).ensureAppleHiddenWebAudio &&
              (this as any).ensureAppleHiddenWebAudio(rid);
          } catch {}
          // 在用户手势内绑定并启动 <audio> sink，保证 iOS 后台持续输出
          try {
            (this as any).ensureAppleWASink &&
              (this as any).ensureAppleWASink(rid);
          } catch {}
          // 标记为 WA-only 房间，避免 HTML5 链介入
          try {
            const g: any = globalThis as any;
            if (!g.__appleUseWAOnlyRooms) g.__appleUseWAOnlyRooms = new Set();
            g.__appleUseWAOnlyRooms.add(rid);
          } catch {}
          // 冻结 Howler，避免干扰
          try {
            const st = this.roomStates.get(rid);
            const hp: any = st && (st as any).howlerPlayer;
            if (hp && typeof hp.setAppleHiddenFreeze === "function") {
              hp.setAppleHiddenFreeze(true);
            }
            if (hp && typeof hp.setMuted === "function") {
              hp.setMuted(true);
            }
          } catch {}
        }
      } catch {}
    } catch {}
    const res = await startProductsLive({
      roomId: state.productList.id
    });
    // 延时一秒配合后端需求
    await delay(1000);
    console.warn("start live await 1s  ");
    if (res && res.success) {
      state.productList.status = "live";

      // 关键修复：直接使用开始直播接口返回的sessionId
      if (res.result && res.result.sessionId) {
        state.sessionId = res.result.sessionId;
        console.log(`✅ 开始直播成功，获取到sessionId: ${state.sessionId}`);
      } else {
        message.warning(res.message || res.result, true);
        state.startLiveLoading = false;
        state.productList.status = "ended";
        this.roomStates.set(state.roomId || state.productList.id, state);
        return;
      }

      // 不清空队列，保持现有的播放状态
      // state.queue = []; // 移除：开始直播不需要清空队列
      // state.currentIndex = 0; // 移除：保持当前索引
      state.lastRecoveryTime = null; // 新开始直播，清除恢复标记

      // 关键修复：开始新直播时清空之前可能残留的播放时间
      state.currentAudioTime = undefined;
      console.log(`开始新直播，清空之前的播放时间状态`);

      // 移除所有去重保护逻辑，让每个后端推送的音频都能正常播放
      console.log(`开始直播，去除所有去重限制`);

      // 修复：立即设置用户交互状态，确保音频可以有声播放
      this.userHasInteracted = true;
      localStorage.setItem("chrome_autoplay_explicitly_set", "true");

      // 🚀 强制重置播放状态，确保可以开始新的播放
      state.isPlaying = false;

      // 修复：确保在发送bind消息前WebSocket已连接
      if (state.commentWs && state.commentWs.readyState === WebSocket.OPEN) {
        this.sendBindMessage();
      }

      // 保存状态变化
      this.saveStateToStorage();

      // 🚀 确保音频WebSocket连接正常，确保sessionId存在后再连接
      if (
        !state.audioQueueWs ||
        state.audioQueueWs.readyState !== WebSocket.OPEN
      ) {
        try {
          console.log(
            `初始化音频WebSocket连接（sessionId: ${state.sessionId}）...`
          );
          await this.initAudioQueueWebSocket(
            state.productList.id,
            state.sessionId
          );
          console.log(`✅ 音频WebSocket连接成功`);
        } catch (error) {
          console.error(`❌ 音频WebSocket连接失败:`, error);
          // WebSocket连接失败也给出提示
          message.warning($t("msg.audioConnectionEstablishFailed"), true);
        }
      }

      // 修复：如果有直播链接，开播后自动绑定评论WebSocket或发送绑定消息
      if (state.roomLink) {
        try {
          if (
            state.isBound &&
            state.commentWs &&
            state.commentWs.readyState === WebSocket.OPEN
          ) {
            // 如果已经绑定了WebSocket，直接发送绑定消息
            // console.log(`🔌 开播成功，WebSocket已连接，发送绑定消息...`);
            this.sendBindMessage();
          } else {
            // 如果还没有绑定WebSocket，建立连接
            // console.log(`🔌 开播成功，自动绑定评论WebSocket...`);
            await this.bindCommentWebSocket();
          }
        } catch (_error) {
          console.warn(`🔌 评论WebSocket绑定失败:`, _error);
          // 不阻塞开播流程
        }
      }

      // 修复：立即更新音频状态，不要延迟
      this.updateAudioStateAfterActivation();

      // 修复：不在这里主动开始播放，让WebSocket收到音频时再开始
      // 这样避免与WebSocket处理逻辑重复
      console.log(`开始直播成功，等待WebSocket推送音频数据`);

      // 如果队列已有音频且没有在播放，才启动播放
      if (state.queue.length > 0 && !state.isPlaying) {
        console.log(
          `检测到队列有音频且未播放 - 等待WebSocket或用户操作触发播放`
        );
        // 注释：减少重复的playNext调用，让WebSocket来驱动播放
        // setTimeout(() => {
        //   this.playNext(state.productList.id);
        // }, 300);
      }

      message.success(
        res.message || $t("msg.startLiveSuccess"),
        res.message ? false : true
      );

      // 🚀 发出直播开始事件，通知其他组件（如 SidebarLeftCollapse）
      emitter.emit("liveStatusChange", {
        action: "start",
        roomId: state.roomId || state.productList.id
      });

      // 🌐 跨浏览器通信：在本地存储中记录直播状态变化
      const liveStatusData = {
        action: "start",
        roomId: state.roomId || state.productList.id,
        timestamp: Date.now()
      };
      localStorage.setItem("liveStatusChange", JSON.stringify(liveStatusData));

      // 修复：同时发出房间状态同步事件
      const roomStatusData = {
        roomId: state.roomId || state.productList.id,
        status: "live" as "live" | "ended" | "preparing",
        roomData: {
          isPlaying: state.isPlaying,
          isBound: state.isBound,
          liveLink: state.liveLink,
          livePlatform: state.livePlatform,
          selectedVoiceId: state.selectedVoiceId,
          selectedProductIds: state.selectedProductIds
        },
        timestamp: Date.now()
      };
      emitter.emit("roomStatusSync", roomStatusData as any);
      localStorage.setItem("roomStatusSync", JSON.stringify(roomStatusData));
      // 同步到服务端与其他设备
      this.syncCurrentRoomStatus();
    } else {
      message.error(
        res?.message || $t("msg.startLiveSuccess"),
        res.message ? false : true
      );
      state.productList.status = "ended";
    }
    state.startLiveLoading = false;
    state.roomId = state.productList.id;
    this.roomStates.set(state.roomId || state.productList.id, state);
  } catch {
    message.error($t("msg.startLiveFailed"), true);
    state.productList.status = "ended";
    state.startLiveLoading = false;
    this.roomStates.set(state.roomId || state.productList.id, state);
  }
}

export async function startExperienceLive(
  this: RootState & { [key: string]: any }
) {
  if (!this.currentRoomState) return;
  const state = this.currentRoomState;
  state.sessionId = Date.now().toString();

  // 移除权限检查，直接开始直播

  state.startLiveLoading = true;
  try {
    // 🍎 首播预热：在用户点击事件栈内尽早建立 Now Playing
    try {
      const rid = state.roomId || state.productList.id;
      this.prewarmNowPlayingForApple && this.prewarmNowPlayingForApple(rid);
      // 🍎 Apple-only: 强制使用 WebAudio 分支
      try {
        const ua = navigator.userAgent || "";
        const isIOS =
          /iPhone|iPad|iPod/i.test(ua) ||
          (/Macintosh/.test(ua) && (navigator as any).maxTouchPoints > 1);
        const isSafari =
          /Safari/i.test(ua) && !/Chrome|Chromium|Edge/i.test(ua);
        const appleLike = isIOS || isSafari;
        if (appleLike) {
          try {
            augmentStoreWithAppleHiddenWebAudio(this as any);
          } catch {}
          try {
            (this as any).ensureAppleHiddenWebAudio &&
              (this as any).ensureAppleHiddenWebAudio(rid);
          } catch {}
          // 在用户手势内绑定并启动 <audio> sink，保证 iOS 后台持续输出
          try {
            (this as any).ensureAppleWASink &&
              (this as any).ensureAppleWASink(rid);
          } catch {}
          // 标记为 WA-only 房间，避免 HTML5 链介入
          try {
            const g: any = globalThis as any;
            if (!g.__appleUseWAOnlyRooms) g.__appleUseWAOnlyRooms = new Set();
            g.__appleUseWAOnlyRooms.add(rid);
          } catch {}
          // 冻结 Howler，避免干扰
          try {
            const st = this.roomStates.get(rid);
            const hp: any = st && (st as any).howlerPlayer;
            if (hp && typeof hp.setAppleHiddenFreeze === "function") {
              hp.setAppleHiddenFreeze(true);
            }
            if (hp && typeof hp.setMuted === "function") {
              hp.setMuted(true);
            }
          } catch {}
        }
      } catch {}
    } catch {}
    state.productList.status = "live";

    // 不清空队列，保持现有的播放状态
    // state.queue = []; // 移除：开始直播不需要清空队列
    // state.currentIndex = 0; // 移除：保持当前索引
    state.lastRecoveryTime = null; // 新开始直播，清除恢复标记

    // 关键修复：开始新直播时清空之前可能残留的播放时间
    state.currentAudioTime = undefined;
    console.log(`开始新直播，清空之前的播放时间状态`);

    // 移除所有去重保护逻辑，让每个后端推送的音频都能正常播放
    console.log(`开始直播，去除所有去重限制`);

    // 修复：立即设置用户交互状态，确保音频可以有声播放
    this.userHasInteracted = true;
    localStorage.setItem("chrome_autoplay_explicitly_set", "true");

    // 🚀 强制重置播放状态，确保可以开始新的播放
    state.isPlaying = false;

    // 保存状态变化
    this.saveStateToStorage();

    // 🚀 确保音频WebSocket连接正常，确保sessionId存在后再连接
    if (
      !state.audioQueueWs ||
      state.audioQueueWs.readyState !== WebSocket.OPEN
    ) {
      try {
        console.log(
          `初始化音频WebSocket连接（sessionId: ${state.sessionId}）...`
        );
        await this.initAudioQueueWebSocket(
          state.productList.id,
          state.sessionId,
          true
        );
        console.log(`✅ 音频WebSocket连接成功`);
      } catch (error) {
        console.error(`❌ 音频WebSocket连接失败:`, error);
        // WebSocket连接失败也给出提示
        message.warning($t("msg.audioConnectionEstablishFailed"), true);
      }
    }

    // 修复：立即更新音频状态，不要延迟
    this.updateAudioStateAfterActivation();

    // 修复：不在这里主动开始播放，让WebSocket收到音频时再开始
    // 这样避免与WebSocket处理逻辑重复
    console.log(`开始直播成功，等待WebSocket推送音频数据`);

    // 如果队列已有音频且没有在播放，才启动播放
    if (state.queue.length > 0 && !state.isPlaying) {
      console.log(`检测到队列有音频且未播放 - 等待WebSocket或用户操作触发播放`);
      // 注释：减少重复的playNext调用，让WebSocket来驱动播放
      // setTimeout(() => {
      //   this.playNext(state.productList.id);
      // }, 300);
    }

    // 🚀 发出直播开始事件，通知其他组件（如 SidebarLeftCollapse）
    emitter.emit("liveStatusChange", {
      action: "start",
      roomId: state.roomId || state.productList.id
    });

    // 🌐 跨浏览器通信：在本地存储中记录直播状态变化
    // const liveStatusData = {
    //   action: "start",
    //   roomId: state.roomId || state.productList.id,
    //   timestamp: Date.now()
    // };
    // localStorage.setItem("liveStatusChange", JSON.stringify(liveStatusData));

    // 修复：同时发出房间状态同步事件
    // const roomStatusData = {
    //   roomId: state.roomId || state.productList.id,
    //   status: "live" as "live" | "ended" | "preparing",
    //   roomData: {
    //     isPlaying: state.isPlaying,
    //     isBound: state.isBound,
    //     liveLink: state.liveLink,
    //     livePlatform: state.livePlatform,
    //     selectedVoiceId: state.selectedVoiceId,
    //     selectedProductIds: state.selectedProductIds
    //   },
    //   timestamp: Date.now()
    // };
    // emitter.emit("roomStatusSync", roomStatusData as any);
    // localStorage.setItem("roomStatusSync", JSON.stringify(roomStatusData));
    // // 同步到服务端与其他设备
    // this.syncCurrentRoomStatus();
    state.startLiveLoading = false;
    state.roomId = state.productList.id;
    this.roomStates.set(state.roomId || state.productList.id, state);
  } catch {
    message.error($t("msg.startLiveFailed"), true);
    state.startLiveLoading = false;
  }
}

export async function endLive(this: RootState & { [key: string]: any }) {
  if (!this.currentRoomState) return false;

  emitter.emit("stopPlayHistory");
  const state = this.currentRoomState;

  state.endLiveLoading = true;
  try {
    const res = await endProductsLive({
      roomId: state.roomId || state.productList.id
    });
    if (res && res.success) {
      state.productList.status = "ended";
      state.isPlaying = false;
      state.publicMessages = [];
      // state.roomLink = "";

      // 关闭轮播
      state.isCarouselPlaying = false;

      // 清空直播数据信息
      state.currentDashbordForm = {};
      // 🔴 立即停止所有音频播放 - 最高优先级，直接放在API响应成功的最前面

      // 1. 立即强制中断所有可能的音频播放
      try {
        // 直接在Web Audio API层面强制停止所有声音
        if (window.AudioContext || window.webkitAudioContext) {
          // 尝试获取并暂停所有活跃的音频上下文
          const AudioContext =
            window.AudioContext || (window as any).webkitAudioContext;
          const audioContext = new AudioContext();
          if (audioContext && audioContext.state === "running") {
            audioContext.suspend().then(() => {
              console.log("🔴 音频上下文已强制暂停");
              // 尝试恢复后立即关闭，彻底中断音频
              audioContext.resume().then(() => audioContext.close());
            });
          }
        }
      } catch (error) {
        console.warn("🔴 Web Audio API强制停止失败:", error);
      }

      // 2. 强制清空队列，阻止后续音频播放
      if (state.queue && Array.isArray(state.queue)) {
        state.queue = [];
        state.currentIndex = 0;
      }

      // 3. 立即设置播放状态为false
      state.isPlaying = false;

      // 4. 强制停止Howler播放器
      if ((state as any).howlerPlayer) {
        try {
          const howlerPlayer = (state as any).howlerPlayer;
          // 多重停止保障：先停止所有可能的ID
          if (howlerPlayer.state() === "playing") {
            console.log("🔴 强制停止Howler播放");
            howlerPlayer.stop();
          }
          // 尝试直接操作内部状态
          if (
            (howlerPlayer as any)._sounds &&
            Array.isArray((howlerPlayer as any)._sounds)
          ) {
            (howlerPlayer as any)._sounds.forEach((sound: any) => {
              try {
                if (sound._node) {
                  // 直接断开音频节点连接
                  sound._node.disconnect();
                }
                if (sound.stop) sound.stop();
              } catch {}
            });
          }
          // 立即卸载所有资源
          howlerPlayer.unload();
          howlerPlayer.destroy();
          (state as any).howlerPlayer = null;
        } catch (error) {
          console.error(`🚫 清理Howler播放器失败:`, error);
          (state as any).howlerPlayer = null;
        }
      }

      // 5. 强制停止UniversalWebAudio播放器
      if ((state as any).universalWebAudioPlayer) {
        try {
          this.destroyUniversalWebAudio(state.roomId || state.productList.id);
          const player = (state as any).universalWebAudioPlayer;
          console.log(
            `🔴 [UniversalWebAudio] 强制停止播放器 room=${state.roomId || state.productList.id}`
          );

          // 直接操作播放器内部的音频源节点（如果可访问）
          if (
            player &&
            (player as any).audioContext &&
            (player as any).audioContext.state === "running"
          ) {
            (player as any).audioContext.suspend();
          }

          // 强制停止并清空队列
          player && player.stop && player.stop();
          if (player && typeof player.clearQueue === "function") {
            player.clearQueue();
          } else if (
            player &&
            (player as any).queue &&
            Array.isArray((player as any).queue)
          ) {
            (player as any).queue = [];
          }

          // 销毁播放器
          if (player && typeof player.unload === "function") {
            player.unload();
          }
          player && player.destroy();
          (state as any).universalWebAudioPlayer = null;
        } catch (error) {
          console.error(`🚫 [UniversalWebAudio] 清理播放器失败:`, error);
          (state as any).universalWebAudioPlayer = null;
        }
      }

      // 6. 强制清理Howler全局实例
      try {
        const g: any = globalThis as any;
        if (g && g.Howler) {
          // 强制停止所有howl实例
          if (g.Howler._howls && Array.isArray(g.Howler._howls)) {
            g.Howler._howls.forEach((howl: any) => {
              try {
                // 直接停止所有可能的ID
                howl.stop();
                // 卸载资源
                howl.unload();
                // 尝试销毁
                if (howl.destroy) howl.destroy();
              } catch {}
            });
            // 清空实例列表
            g.Howler._howls = [];
          }

          // 直接使用Howler全局静音
          if (g.Howler.mute) {
            g.Howler.mute(true);
          }
        }
      } catch (error) {
        console.warn(`⚠️ 清理全局Howler实例失败:`, error);
      }

      // 7. 强制重置所有播放状态
      try {
        // 清理所有可能的播放状态标记
        delete (state as any).__skipUniversalPlayId;
        delete (state as any).__universalCurrentId;
        delete (state as any).__universalCurrentStartedAt;
        delete (state as any).howlerEnqueuedIds;

        // 额外的播放状态清理
        (state as any).currentAudioTime = undefined;
        (state as any).isPreloading = false;
        (state as any).isRestoringPlayback = false;
        (state as any).readyToRestore = false;
      } catch (error) {
        console.warn(`⚠️ 清理播放状态标记失败:`, error);
      }

      // 8. 额外的DOM级别的音频元素检查和清理
      try {
        // 查找并停止所有页面上的音频元素
        const audioElements = document.querySelectorAll("audio, video");
        audioElements.forEach(element => {
          try {
            (element as HTMLAudioElement | HTMLVideoElement).pause();
            (element as HTMLAudioElement | HTMLVideoElement).currentTime = 0;
          } catch {}
        });
      } catch (error) {
        console.warn("⚠️ DOM音频元素清理失败:", error);
      }

      // 获取当前房间ID
      const currentRoomId = state.roomId || state.productList.id;

      // 强化：停止后台定时器
      if (this.cleanupBackgroundSupport) {
        try {
          this.cleanupBackgroundSupport();
        } catch (error) {
          console.warn(`后台清理失败:`, error);
        }
      }

      // 修复：仅销毁当前房间的苹果隐藏态 WebAudio 分支
      try {
        const currentRoomId = state.roomId || state.productList.id;
        const rs = this.roomStates.get(currentRoomId);

        // 清理当前房间的 WA 播放器状态（如存在）
        if (rs && (rs as any).__appleWA__) {
          try {
            (this as any).destroyAppleHiddenWebAudio &&
              (this as any).destroyAppleHiddenWebAudio(currentRoomId);
          } catch {}
          try {
            (rs as any).__appleWA__ = null;
          } catch {}
        }
        // 清理当前房间的 HTML5 链式播放器（苹果端分支）
        if (rs && (rs as any).__appleHtml5Chain__) {
          try {
            (this as any).destroyAppleHtml5Chain &&
              (this as any).destroyAppleHtml5Chain(currentRoomId);
          } catch {}
          try {
            (rs as any).__appleHtml5Chain__ = null;
          } catch {}
        }
      } catch {}

      // 修复：正确断开WebSocket连接
      if (state.commentWs) {
        state.commentWs.close();
        state.commentWs = null;
      }
      if (state.audioQueueWs) {
        state.audioQueueWs.close();
        state.audioQueueWs = null;
      }

      // 重置重连计数器
      state.commentWsReconnectCount = 0;
      state.audioQueueWsReconnectCount = 0;
      state.isBound = false;
      // state.clearQueue();
      // 关键修复：仅清空当前房间的队列和进度计时器
      console.log(currentRoomId);

      this.clearQueue(currentRoomId);
      state.currentIndex = 0;

      // 关键修复：清空保存的播放时间，防止下次开播时继续播放
      state.currentAudioTime = undefined;

      // 新增：清除待切换产品状态和loading状态
      state.pendingProductSwitch = null;
      if (state.cardLoadingMap) {
        Object.keys(state.cardLoadingMap).forEach(productId => {
          state.cardLoadingMap[productId] = false;
        });
      }

      // 关键修复：清空公屏消息，防止下次进入时显示旧消息
      state.publicMessages = [];

      // 关键修复：重置所有播放相关状态
      state.isPlaying = false;
      state.isPreloading = false;
      // 旧播放器属性已移除，使用 Howler.js 统一管理
      state.readyToRestore = false;
      // state.isRestoringPlayback = false; // 移除不存在的属性
      // 清空插播列表
      state.insertSegmentList = [];

      // 关键修复：确保状态完全清理后再保存
      this.saveStateToStorage();

      message.success(
        res.message || $t("msg.closeLiveSucceeded"),
        res.message ? false : true
      );

      // 🚀 发出直播结束事件，通知其他组件（如 SidebarLeftCollapse）
      emitter.emit("liveStatusChange", {
        action: "end",
        roomId: state.roomId || state.productList.id
      });

      // 🌐 跨浏览器通信：在本地存储中记录直播状态变化
      const liveStatusData = {
        action: "end",
        roomId: state.roomId || state.productList.id,
        timestamp: Date.now()
      };
      localStorage.setItem("liveStatusChange", JSON.stringify(liveStatusData));

      // 修复：同时发出房间状态同步事件
      const roomStatusData = {
        roomId: state.roomId || state.productList.id,
        status: "ended" as "live" | "ended" | "preparing",
        roomData: {
          isPlaying: false,
          isBound: false,
          liveLink: "",
          livePlatform: state.livePlatform,
          selectedVoiceId: state.selectedVoiceId,
          selectedProductIds: state.selectedProductIds
        },
        timestamp: Date.now()
      };
      emitter.emit("roomStatusSync", roomStatusData as any);
      localStorage.setItem("roomStatusSync", JSON.stringify(roomStatusData));
      // 同步到服务端与其他设备
      this.syncCurrentRoomStatus();

      // const userStore = useUserStoreHook();
      // userStore.forceRefreshUserInfo();
      return true;
    } else {
      message.error(
        res?.message || $t("msg.closeLiveFailed"),
        res.message ? false : true
      );
      return false;
    }
  } catch (error) {
    console.error("关闭直播失败:", error);
    message.error($t("msg.closeLiveFailed"), true);
    return false;
  } finally {
    state.endLiveLoading = false;
  }
}

export async function endExperienceLive(
  this: RootState & { [key: string]: any }
) {
  if (!this.currentRoomState) return false;
  const state = this.currentRoomState;

  state.endLiveLoading = true;
  try {
    state.productList.status = "ended";
    state.isPlaying = false;
    state.publicMessages = [];
    // state.roomLink = "";

    // 关闭轮播
    state.isCarouselPlaying = false;

    // 清空直播数据信息
    state.currentDashbordForm = {};
    // 🔴 立即停止所有音频播放 - 最高优先级，直接放在API响应成功的最前面

    // 1. 立即强制中断所有可能的音频播放
    try {
      // 直接在Web Audio API层面强制停止所有声音
      if (window.AudioContext || window.webkitAudioContext) {
        // 尝试获取并暂停所有活跃的音频上下文
        const AudioContext =
          window.AudioContext || (window as any).webkitAudioContext;
        const audioContext = new AudioContext();
        if (audioContext && audioContext.state === "running") {
          audioContext.suspend().then(() => {
            console.log("🔴 音频上下文已强制暂停");
            // 尝试恢复后立即关闭，彻底中断音频
            audioContext.resume().then(() => audioContext.close());
          });
        }
      }
    } catch (error) {
      console.warn("🔴 Web Audio API强制停止失败:", error);
    }

    // 2. 强制清空队列，阻止后续音频播放
    if (state.queue && Array.isArray(state.queue)) {
      state.queue = [];
      state.currentIndex = 0;
    }

    // 3. 立即设置播放状态为false
    state.isPlaying = false;

    // 4. 强制停止Howler播放器
    if ((state as any).howlerPlayer) {
      try {
        const howlerPlayer = (state as any).howlerPlayer;
        // 多重停止保障：先停止所有可能的ID
        if (howlerPlayer.state() === "playing") {
          console.log("🔴 强制停止Howler播放");
          howlerPlayer.stop();
        }
        // 尝试直接操作内部状态
        if (
          (howlerPlayer as any)._sounds &&
          Array.isArray((howlerPlayer as any)._sounds)
        ) {
          (howlerPlayer as any)._sounds.forEach((sound: any) => {
            try {
              if (sound._node) {
                // 直接断开音频节点连接
                sound._node.disconnect();
              }
              if (sound.stop) sound.stop();
            } catch {}
          });
        }
        // 立即卸载所有资源
        howlerPlayer.unload();
        howlerPlayer.destroy();
        (state as any).howlerPlayer = null;
      } catch (error) {
        console.error(`🚫 清理Howler播放器失败:`, error);
        (state as any).howlerPlayer = null;
      }
    }

    // 5. 强制停止UniversalWebAudio播放器
    if ((state as any).universalWebAudioPlayer) {
      this.destroyUniversalWebAudio(state.roomId || state.productList.id);
    }

    // 6. 强制清理Howler全局实例
    try {
      const g: any = globalThis as any;
      if (g && g.Howler) {
        // 强制停止所有howl实例
        if (g.Howler._howls && Array.isArray(g.Howler._howls)) {
          g.Howler._howls.forEach((howl: any) => {
            try {
              // 直接停止所有可能的ID
              howl.stop();
              // 卸载资源
              howl.unload();
              // 尝试销毁
              if (howl.destroy) howl.destroy();
            } catch {}
          });
          // 清空实例列表
          g.Howler._howls = [];
        }

        // 直接使用Howler全局静音
        if (g.Howler.mute) {
          g.Howler.mute(true);
        }
      }
    } catch (error) {
      console.warn(`⚠️ 清理全局Howler实例失败:`, error);
    }

    // 7. 强制重置所有播放状态
    try {
      // 清理所有可能的播放状态标记
      delete (state as any).__skipUniversalPlayId;
      delete (state as any).__universalCurrentId;
      delete (state as any).__universalCurrentStartedAt;
      delete (state as any).howlerEnqueuedIds;

      // 额外的播放状态清理
      (state as any).currentAudioTime = undefined;
      (state as any).isPreloading = false;
      (state as any).isRestoringPlayback = false;
      (state as any).readyToRestore = false;
    } catch (error) {
      console.warn(`⚠️ 清理播放状态标记失败:`, error);
    }

    // 8. 额外的DOM级别的音频元素检查和清理
    try {
      // 查找并停止所有页面上的音频元素
      const audioElements = document.querySelectorAll("audio, video");
      audioElements.forEach(element => {
        try {
          (element as HTMLAudioElement | HTMLVideoElement).pause();
          (element as HTMLAudioElement | HTMLVideoElement).currentTime = 0;
        } catch {}
      });
    } catch (error) {
      console.warn("⚠️ DOM音频元素清理失败:", error);
    }

    // 获取当前房间ID
    const currentRoomId = state.roomId || state.productList.id;

    // 强化：停止后台定时器
    if (this.cleanupBackgroundSupport) {
      try {
        this.cleanupBackgroundSupport();
      } catch (error) {
        console.warn(`后台清理失败:`, error);
      }
    }

    // 修复：仅销毁当前房间的苹果隐藏态 WebAudio 分支
    try {
      const currentRoomId = state.roomId || state.productList.id;
      const rs = this.roomStates.get(currentRoomId);

      // 清理当前房间的 WA 播放器状态（如存在）
      if (rs && (rs as any).__appleWA__) {
        try {
          (this as any).destroyAppleHiddenWebAudio &&
            (this as any).destroyAppleHiddenWebAudio(currentRoomId);
        } catch {}
        try {
          (rs as any).__appleWA__ = null;
        } catch {}
      }
      // 清理当前房间的 HTML5 链式播放器（苹果端分支）
      if (rs && (rs as any).__appleHtml5Chain__) {
        try {
          (this as any).destroyAppleHtml5Chain &&
            (this as any).destroyAppleHtml5Chain(currentRoomId);
        } catch {}
        try {
          (rs as any).__appleHtml5Chain__ = null;
        } catch {}
      }
    } catch {}

    // 修复：正确断开WebSocket连接
    if (state.commentWs) {
      state.commentWs.close();
      state.commentWs = null;
    }
    if (state.audioQueueWs) {
      state.audioQueueWs.close();
      state.audioQueueWs = null;
    }

    // 重置重连计数器
    state.commentWsReconnectCount = 0;
    state.audioQueueWsReconnectCount = 0;
    state.isBound = false;
    // state.clearQueue();
    // 关键修复：仅清空当前房间的队列和进度计时器
    console.log(currentRoomId);

    this.clearQueue(currentRoomId);
    state.currentIndex = 0;

    // 关键修复：清空保存的播放时间，防止下次开播时继续播放
    state.currentAudioTime = undefined;

    // 新增：清除待切换产品状态和loading状态
    state.pendingProductSwitch = null;
    if (state.cardLoadingMap) {
      Object.keys(state.cardLoadingMap).forEach(productId => {
        state.cardLoadingMap[productId] = false;
      });
    }

    // 关键修复：清空公屏消息，防止下次进入时显示旧消息
    state.publicMessages = [];

    // 关键修复：重置所有播放相关状态
    state.isPlaying = false;
    state.isPreloading = false;
    // 旧播放器属性已移除，使用 Howler.js 统一管理
    state.readyToRestore = false;
    return true;
  } catch (error) {
    console.error("关闭直播失败:", error);
    message.error($t("msg.closeLiveFailed"), true);
    return false;
  } finally {
    state.endLiveLoading = false;
    this.currentRoomId = null;
  }
}

// 外部房间ID相关函数
export async function getExternalRoomId(
  this: RootState & { [key: string]: any },
  roomLink: string
) {
  if (!roomLink || !roomLink.trim()) {
    throw new Error("房间链接不能为空");
  }

  console.log(`🔗 调用getExternalRoomId API，链接: ${roomLink}`);
  try {
    const res = await getExternalRoomIdApi(roomLink.trim());
    if (res && res.success) {
      const externalRoomId =
        res.result?.externalRoomId || res.result?.roomId || res.result;
      console.log(`✅ 成功获取外部房间ID: ${externalRoomId}`);
      return externalRoomId;
    } else {
      console.warn(`⚠️ 获取外部房间ID失败:`, res);
      throw new Error(res?.message || "获取外部房间ID失败");
    }
  } catch (error) {
    console.error(`❌ 获取外部房间ID出错:`, error);
    throw error;
  }
}

export async function mockAsk(this: RootState & { [key: string]: any }) {
  if (!this.currentRoomState) return;
  const state = this.currentRoomState;

  // 优化：添加更友好的提示信息
  if (!state.sendMsg || !state.sendMsg.trim()) {
    return message.warning($t("msg.enterSimulatedCommentContent"), true);
  }

  // 优化：检查直播间状态，提供更详细的提示
  if (state.productList.status !== "live") {
    return message.error($t("msg.liveRoomNotBroadcasting"), true);
  }

  state.simulateCommentLoading = true;

  try {
    let externalRoomId = null;

    // 新增：如果有绑定的直播链接，先获取外部房间ID
    if (state.roomLink && state.roomLink.trim()) {
      console.log(`🔗 获取外部房间ID，链接: ${state.roomLink}`);
      try {
        const externalRes = await getExternalRoomIdApi(state.roomLink.trim());
        if (externalRes && externalRes.success && externalRes.result) {
          externalRoomId =
            externalRes.result.externalRoomId ||
            externalRes.result.roomId ||
            externalRes.result;
          console.log(`✅ 获取到外部房间ID: ${externalRoomId}`);
        } else {
          console.warn(`⚠️ 获取外部房间ID失败:`, externalRes);
        }
      } catch (error) {
        console.error(`❌ 获取外部房间ID出错:`, error);
      }
    }
    const params = {
      roomId: state.roomId || state.productList.id,
      content: state.sendMsg,
      externalRoomId: externalRoomId, // 添加外部房间ID
      username: "模拟用户"
    };

    console.log(`📤 发送模拟评论请求:`, params);
    const res = await mockAskApi(params);
    if (res.success) {
      message.success($t("msg.simulateCommentSuccess"), true);
      state.sendMsg = "";
    } else {
      message.error(res.message || $t("msg.simulateCommentFail"), true);
    }
  } finally {
    state.simulateCommentLoading = false;
  }
}

export async function sendMessage(this: RootState & { [key: string]: any }) {
  if (!this.currentRoomState) return;
  const state = this.currentRoomState;

  // 优化：添加更友好的提示信息
  if (!state.sendMsg || !state.sendMsg.trim()) {
    return message.warning($t("msg.enterIntercutContent"), true);
  }

  // 优化：检查直播间状态，提供更详细的提示
  if (state.productList.status !== "live") {
    return message.error($t("msg.liveRoomNotBroadcastingForIntercut"), true);
  }

  // 优化：检查是否有sessionId
  if (!state.sessionId) {
    return message.error($t("msg.liveConnectionNotEstablished"), true);
  }

  state.sendMessageLoading = true;
  try {
    try {
      console.warn(
        `[urgent-check] sendMessage->insertUrgent attempt sessionId=${state.sessionId} text="${(state.sendMsg || "").substring(0, 30)}..."`
      );
    } catch {}
    const translatedMsg = await translateSingleText(
      state.sendMsg,
      state.productDetail.language
    );
    const res = await insertUrgent({
      text: JSON.parse(translatedMsg.result.translatedText).text,
      sessionId: state.sessionId
    });
    try {
      console.warn(
        `[urgent-check] sendMessage->insertUrgent response success=${!!(res && res.success)} code=${res?.code || "NA"} msg=${res?.message || "NA"}`
      );
    } catch {}
    if (res && res.success) {
      message.success(
        res.message || $t("msg.intercutMessageSentSuccess"),
        res.message ? false : true
      );
      state.sendMsg = "";
    } else {
      message.error(
        res?.message || $t("msg.intercutMessageSentFail"),
        res.message ? false : true
      );
    }
  } catch (error) {
    console.error("插播失败:", error);
    message.error(
      `${$t("msg.intercutMessageSentFail")}，${$t("msg.checkNetworkConnection")}`,
      true
    );
  } finally {
    state.sendMessageLoading = false;
  }
}

// 清理房间音频队列,保留当前播放项之前的所有项
export function clearAudioQueueAfterCurrent(
  this: RootState & { [key: string]: any },
  roomId: string
) {
  const state = this.roomStates.get(roomId);
  if (!state) {
    console.warn(`房间${roomId}不存在`);
    return;
  }

  // 1. 记录当前正在播放的索引
  const currentIndex = state.currentIndex;

  console.log(`🗑️ 清理房间${roomId}的音频队列,保留索引${currentIndex}之前的项`);

  // 2. 删除 state.queue 中当前索引之后的所有项
  const removedItems = state.queue.splice(currentIndex + 1);
  console.log(`🗑️ 从 state.queue 删除了 ${removedItems.length} 项`);

  // 3. 清理 UniversalWebAudio 播放器(PC/Android Chrome)
  try {
    const g: any = globalThis as any;
    const useUniversalWebAudio = !!(
      g &&
      g.__universalWebAudioRooms &&
      typeof g.__universalWebAudioRooms.has === "function" &&
      g.__universalWebAudioRooms.has(roomId)
    );

    if (useUniversalWebAudio) {
      const player = (state as any).universalWebAudioPlayer;
      if (player) {
        console.log(`🗑️ 清理 UniversalWebAudio 播放器队列`);

        // 停止当前播放(如果需要)
        // player.stop();

        // 清理播放器内部队列(需要访问内部 queue)
        player.player.playlist.splice(currentIndex + 1);

        if ((player as any).queue) {
          const playerQueue = (player as any).queue;
          const removedFromPlayer = playerQueue.splice(currentIndex + 1);
          console.log(`🗑️ 从播放器删除了 ${removedFromPlayer.length} 项`);

          // 清理 addedIds Set
          if ((player as any).addedIds) {
            removedFromPlayer.forEach((item: any) => {
              if (item.id) {
                (player as any).addedIds.delete(item.id);
              }
            });
          }
        }

        // 如果播放器支持清理方法,调用它
        if (typeof (player as any).clearQueueAfter === "function") {
          (player as any).clearQueueAfter(currentIndex);
        }
      }
    }
  } catch (error) {
    console.error("清理 UniversalWebAudio 播放器失败:", error);
  }

  // 4. 清理 Apple WebAudio 播放器(iOS/Safari)
  try {
    const appleWA = (state as any).__appleWA__;
    if (appleWA && appleWA.player) {
      console.log(`🗑️ 清理 Apple WebAudio 播放器队列`);

      // 清理播放器内部队列
      if (appleWA.player.queue) {
        const appleQueue = appleWA.player.queue;
        const removedFromApple = appleQueue.splice(currentIndex + 1);
        console.log(`🗑️ 从 Apple 播放器删除了 ${removedFromApple.length} 项`);

        // 清理 addedIds
        if (appleWA.player.addedIds) {
          removedFromApple.forEach((item: any) => {
            if (item.id) {
              appleWA.player.addedIds.delete(item.id);
            }
          });
        }
      }

      // 如果支持清理方法
      if (typeof appleWA.player.clearQueueAfter === "function") {
        appleWA.player.clearQueueAfter(currentIndex);
      }
    }
  } catch (error) {
    console.error("清理 Apple WebAudio 播放器失败:", error);
  }

  // 5. 清理 Howler 播放器(其他情况)
  try {
    const howlerPlayer = (state as any).howlerPlayer;
    if (howlerPlayer) {
      console.log(`🗑️ 清理 Howler 播放器队列`);

      // Howler 播放器通常有内部播放列表
      if (typeof howlerPlayer.clearQueueAfter === "function") {
        howlerPlayer.clearQueueAfter(currentIndex);
      } else {
        // 如果没有专门的清理方法,停止后续播放
        // 这需要根据你的 HowlerSeamlessPlayer 实现来调整
        const status = howlerPlayer.getStatus();
        if (status && status.playlist) {
          // 清理 Howler 内部播放列表
          status.playlist.splice(currentIndex + 1);
        }
      }
    }
  } catch (error) {
    console.error("清理 Howler 播放器失败:", error);
  }

  // 6. 清理入队记录(防止重复添加)
  if ((state as any).howlerEnqueuedIds) {
    removedItems.forEach(item => {
      if (item.id) {
        (state as any).howlerEnqueuedIds.delete(item.id);
      }
    });
  }

  // 7. 保存状态
  try {
    this.saveStateToStorage();
  } catch (error) {
    console.error("保存状态失败:", error);
  }

  console.log(`✅ 音频队列清理完成,当前队列长度: ${state.queue.length}`);
}
